import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { NotifyService, NotifyType } from '../shared/services/notify';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const notify = inject(NotifyService);

  if (req.url.includes('/auth/refresh') || req.url.includes('/auth/sign-in')) {
    return next(req);
  }

  const addToken = (request: HttpRequest<unknown>) => {
    const token = authService.getAccessToken();
    if (!token) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const handle401Error = (
    request: HttpRequest<unknown>,
    handler: HttpHandlerFn,
  ) => {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      const refreshToken = authService.getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        authService.expireSession();
        return throwError(() => new Error('No refresh token available'));
      }

      return authService.refreshAccessToken().pipe(
        switchMap((user) => {
          isRefreshing = false;
          authService.updateTokens(user.access, user.refresh);

          authService.updateStoredUser({
            roles: user.roles,
            permissions: user.permissions,
            is_admin: user.is_admin,
          } as never);

          refreshTokenSubject.next(user.access.token);
          return handler(addToken(request));
        }),
        catchError((err) => {
          isRefreshing = false;
          refreshTokenSubject.next(null);
          authService.expireSession();
          return throwError(() => err);
        }),
      );
    }

    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap(() => handler(addToken(request))),
    );
  };

  const authReq = addToken(req);

  return next(authReq).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(authReq, next);
      }

      if (error instanceof HttpErrorResponse && error.status === 403) {
        notify.show({
          title: 'Not allowed',
          text:
            error.error?.message ?? 'You do not have permission to do that.',
          type: NotifyType.Danger,
        });
      }

      return throwError(() => error);
    }),
  );
};
